import { MODULE_LOCAL_URL_PREFIX, MODULE_STORAGE_URL_PREFIX } from './bundle';

/**
 * Path prefix a browser runtime serves module assets under (见 18 / 20):
 * `<origin>/_mod/<moduleId>/<root-relative path>`, the http(s) counterpart of
 * the desktop's `hibro-assets://<moduleId>/<path>`. Single source of truth for
 * the web host's asset base, for the static route that answers those requests,
 * and for the build's copy target.
 *
 * The `_` marks it as a **platform** path (`PLATFORM_PATH_PREFIX`, 见 20), which
 * is what frees the origin's root for module deep links: on web an unmarked
 * `/<moduleId>/…` addresses the module itself, a marked `/_mod/<moduleId>/…`
 * addresses its files.
 */
export const MODULE_ASSET_PATH_PREFIX = '/_mod/';

/**
 * Query parameter asking a browser runtime to serve a `@storage` URL **as a
 * download** rather than for inline rendering (见 18): its value is the filename
 * to offer, and the Service Worker answers with `Content-Disposition:
 * attachment`.
 *
 * This is what lets "hand the user this file" cost nothing in page memory: the
 * bytes go OPFS → Service Worker → the browser's download manager, never
 * through a `Blob` the page has to hold. So there is no large-file special case
 * — 1 KB and 1 GB take the same route.
 *
 * It has to be a shared constant because the producer (the web host resolving
 * `capabilities.saveStorageFile`) and the consumer (the Service Worker) are
 * separate bundles that only meet over a URL.
 *
 * In the **query** rather than the path: the path addresses a file, and the same
 * file must keep one address whether it is being played inline or saved — a
 * marker segment would fork that.
 */
export const STORAGE_DOWNLOAD_PARAM = 'download';

/**
 * Serving-agnostic half of module asset addressing (见 12 / 18).
 *
 * The desktop serves module assets from disk behind the privileged `hibro-assets://`
 * scheme; a browser runtime serves the same tree over http(s) from a static
 * route (and later, from OPFS behind a Service Worker). Those differ in how a
 * request *arrives* and how bytes are *fetched*, but they must apply the exact
 * same confinement policy — so the policy lives here, with no filesystem and no
 * URL-scheme assumptions, and each runtime keeps only its own I/O.
 */

/**
 * Whether a path segment is unsafe to resolve under a module root.
 *
 * Rejects parent traversal, backslashes (a Windows separator smuggled through a
 * single URL segment), drive-letter colons, and C0 controls (incl. NUL, which
 * can truncate a path in native APIs).
 */
export function isUnsafeAssetSegment(segment: string): boolean {
  return (
    segment === '..' ||
    segment.includes('\\') ||
    segment.includes(':') ||
    // eslint-disable-next-line no-control-regex -- reject C0 controls (incl. NUL)
    /[\u0000-\u001f]/.test(segment)
  );
}

/**
 * Whether a module id is unsafe to use as a single directory name under
 * `hibro-modules/` (见 07 / §5.13).
 *
 * {@link isUnsafeAssetSegment} is for *already-split* path segments: it does
 * not reject `.` (the splitter drops those) or `/`. A module id *is* the
 * directory name, so `path.join(userData, 'hibro-modules', id)` with `id` of
 * `..` or `.` would resolve to `userData` or `hibro-modules` itself.
 *
 * The `_` prefix is a namespace reservation ({@link isReservedModuleId}), not a
 * path-escape, and is checked separately by `validateManifest`.
 */
export function isUnsafeModuleId(id: string): boolean {
  return (
    id === '' || id === '.' || id.includes('/') || isUnsafeAssetSegment(id)
  );
}

/**
 * Split an already-decoded path into safe segments, or `undefined` if any
 * segment could climb out of the root. Empty and `.` segments are dropped (a
 * URL parser normalizes those away already; the guard is defense in depth).
 *
 * Fail closed: callers treat `undefined` as 404 rather than clamping.
 */
export function splitSafeAssetPath(pathname: string): string[] | undefined {
  const segments = pathname
    .split('/')
    .filter((segment) => segment !== '' && segment !== '.');
  return segments.some(isUnsafeAssetSegment) ? undefined : segments;
}

/**
 * Which root a module request addresses (见 12 / §5.13). Two reserved first
 * segments, everything else is the module's code/asset root:
 *
 * - `@storage` → the module's isolated **data** root ({@link MODULE_STORAGE_URL_PREFIX}).
 * - `@local` → a **grant token** for a file outside every root
 *   ({@link MODULE_LOCAL_URL_PREFIX}); `rest` is that one token. The path
 *   itself is not in the URL — the serving side looks the token up (desktop
 *   only; web 404s, 见 18).
 *
 * `rest` is the remaining path. Returns `undefined` for an empty path, and for a
 * reserved prefix with nothing after it — neither addresses a file.
 */
export function routeModuleAssetPath(
  segments: readonly string[],
): { kind: 'asset' | 'storage' | 'local'; rest: string[] } | undefined {
  if (segments.length === 0) {
    return undefined;
  }
  const reserved =
    segments[0] === MODULE_STORAGE_URL_PREFIX
      ? 'storage'
      : segments[0] === MODULE_LOCAL_URL_PREFIX
        ? 'local'
        : undefined;
  if (!reserved) {
    return { kind: 'asset', rest: [...segments] };
  }
  const rest = segments.slice(1);
  return rest.length === 0 ? undefined : { kind: reserved, rest };
}

/**
 * Outcome of mapping a `Range` request header onto a file of known size.
 * `full` → serve the whole file (no/blank/malformed Range: HTTP says ignore it);
 * `partial` → 206 with inclusive `[start, end]` clamped to the file; `unsatisfiable`
 * → 416 (start past EOF / reversed / empty file).
 */
export type ContentRange =
  | { kind: 'full' }
  | { kind: 'partial'; start: number; end: number }
  | { kind: 'unsatisfiable' };

/**
 * Pure `Range: bytes=…` → byte-range mapping for a `size`-byte file (the caller
 * does the actual I/O). Handles the single-range shapes a media element emits:
 * `bytes=start-end`, open-ended `bytes=start-`, and suffix `bytes=-N` (last N
 * bytes). Multi-range and non-`bytes` units are treated as `full`
 * (spec-compliant: ignore an un-honored Range and return 200). Out-of-bounds or
 * reversed ranges are `unsatisfiable` (→ 416).
 */
export function computeContentRange(
  rangeHeader: string | null | undefined,
  size: number,
): ContentRange {
  if (!rangeHeader) {
    return { kind: 'full' };
  }
  // Single `bytes=start-end` (either side optional). Anything else (multi-range
  // with commas, other units) → ignore the Range and serve the whole file.
  const match = /^bytes=(\d*)-(\d*)$/.exec(rangeHeader.trim());
  if (!match || (match[1] === '' && match[2] === '')) {
    return { kind: 'full' };
  }
  if (size <= 0) {
    return { kind: 'unsatisfiable' };
  }
  // Suffix form `bytes=-N`: the last N bytes.
  if (match[1] === '') {
    const suffix = Number.parseInt(match[2], 10);
    if (suffix <= 0) {
      return { kind: 'unsatisfiable' };
    }
    return {
      kind: 'partial',
      start: Math.max(0, size - suffix),
      end: size - 1,
    };
  }
  const start = Number.parseInt(match[1], 10);
  if (start >= size) {
    return { kind: 'unsatisfiable' };
  }
  const end = match[2] === '' ? size - 1 : Number.parseInt(match[2], 10);
  if (end < start) {
    return { kind: 'unsatisfiable' };
  }
  return { kind: 'partial', start, end: Math.min(end, size - 1) };
}

/**
 * How many leading bytes {@link sniffMediaMimeFromHeader} needs.
 *
 * Exported so each runtime reads exactly this much and no more: the desktop with
 * `fs.read`, a browser runtime with `Blob.slice(0, n).arrayBuffer()`.
 */
export const MEDIA_SNIFF_BYTES = 12;

/**
 * Extension → MIME for files served out of a module's storage root.
 *
 * Two distinct consumers, which is why images belong here even though they are
 * never ranged:
 *
 * - a 206 must declare `Content-Type` **explicitly** — `<video>`/`<audio>` pick a
 *   decoder from it, and no content sniffing happens on a custom scheme;
 * - **anything the user can save** needs it too, because that is where the
 *   browser gets the extension from. An image answered as `application/octet-stream`
 *   still renders (an `<img>` sniffs), but "save image as" then has nothing to go
 *   on and offers `.txt`.
 *
 * **`.svg` and markup types are deliberately absent.** Storage holds files other
 * people sent; on web this origin is the app's own, so answering an attacker's
 * `.svg` with `image/svg+xml` (or anything `text/html`-ish) would let a
 * navigation to that URL run script as the workbench. Falling back to
 * `application/octet-stream` costs an inline SVG preview and buys that back.
 */
export const STORAGE_CONTENT_TYPE: Readonly<Record<string, string>> = {
  '.mp4': 'video/mp4',
  '.m4v': 'video/x-m4v',
  '.webm': 'video/webm',
  '.mov': 'video/quicktime',
  '.ogv': 'video/ogg',
  '.mp3': 'audio/mpeg',
  '.m4a': 'audio/mp4',
  '.aac': 'audio/aac',
  '.wav': 'audio/wav',
  '.ogg': 'audio/ogg',
  '.flac': 'audio/flac',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.jpe': 'image/jpeg',
  '.jfif': 'image/jpeg',
  '.png': 'image/png',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.bmp': 'image/bmp',
  '.heic': 'image/heic',
  '.heif': 'image/heif',
  '.avif': 'image/avif',
};

/**
 * ISO-BMFF major brand → MIME. The container carries images as well as video
 * (HEIC/AVIF are ISO-BMFF), so the brand is the only thing that tells them
 * apart — without this an iPhone photo is served as `video/mp4`.
 */
const ISO_BMFF_BRAND_TYPE: Readonly<Record<string, string>> = {
  qt: 'video/quicktime',
  heic: 'image/heic',
  heix: 'image/heic',
  hevc: 'image/heic',
  hevx: 'image/heic',
  heim: 'image/heif',
  heis: 'image/heif',
  mif1: 'image/heif',
  msf1: 'image/heif',
  avif: 'image/avif',
  avis: 'image/avif',
};

/**
 * Sniff a MIME from a file's first {@link MEDIA_SNIFF_BYTES} bytes, as the
 * fallback when the extension says nothing.
 *
 * This exists because **stored files usually have no extension**: a received one
 * lands under the module's storage root named by id, so extension-only mapping
 * would answer `application/octet-stream` — and then a video refuses to play and
 * a saved image comes out as `.txt`. `undefined` means "unrecognized" — the
 * caller falls back.
 *
 * Pure, so both runtimes share the format knowledge and keep only their own way
 * of reading those bytes (见 18: policy in shared, I/O per runtime).
 */
export function sniffMediaMimeFromHeader(
  header: Uint8Array,
): string | undefined {
  const ascii = (start: number, end: number): string =>
    String.fromCharCode(...header.subarray(start, end));
  if (header.length < 4) {
    return undefined;
  }
  if (header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff) {
    return 'image/jpeg';
  }
  if (
    header[0] === 0x89 &&
    header[1] === 0x50 &&
    header[2] === 0x4e &&
    header[3] === 0x47
  ) {
    return 'image/png';
  }
  if (ascii(0, 4) === 'GIF8') {
    return 'image/gif';
  }
  if (header[0] === 0x42 && header[1] === 0x4d) {
    return 'image/bmp';
  }
  // ISO-BMFF: 'ftyp' at offset 4, then a 4-byte major brand. The brand decides
  // image (HEIC/AVIF) vs video; `qt` is matched by prefix because its real brand
  // is `qt  ` (trailing spaces).
  if (header.length >= 12 && ascii(4, 8) === 'ftyp') {
    const brand = ascii(8, 12);
    return (
      ISO_BMFF_BRAND_TYPE[brand] ??
      ISO_BMFF_BRAND_TYPE[brand.slice(0, 2)] ??
      'video/mp4'
    );
  }
  // EBML (webm/mkv).
  if (
    header[0] === 0x1a &&
    header[1] === 0x45 &&
    header[2] === 0xdf &&
    header[3] === 0xa3
  ) {
    return 'video/webm';
  }
  if (ascii(0, 4) === 'OggS') {
    return 'video/ogg';
  }
  // RIFF containers share a magic; bytes 8-12 pick the form (WebP is one too).
  if (header.length >= 12 && ascii(0, 4) === 'RIFF') {
    const form = ascii(8, 12);
    if (form === 'WAVE') {
      return 'audio/wav';
    }
    if (form === 'WEBP') {
      return 'image/webp';
    }
  }
  if (ascii(0, 3) === 'ID3') {
    return 'audio/mpeg';
  }
  return undefined;
}

/**
 * `Content-Type` by extension for a module's **code root** — the files its own
 * build produced (`dist/index.html`, `ui.js`, an icon, a wasm blob).
 *
 * A separate table from {@link STORAGE_CONTENT_TYPE}, because the two roots have
 * opposite properties and the desktop protocol handler already forks on exactly
 * this distinction (见 18):
 *
 * - a code-root filename comes from the module's build, so its extension is
 *   **trustworthy** and no sniffing is needed;
 * - a stored file is named by id, usually has no extension at all, and holds
 *   bytes someone else sent — hence sniffing there, and no markup types.
 *
 * `.svg` **is** here, unlike in the storage table: a module's own icon is
 * first-party content shipped with the app, not an attacker's upload.
 *
 * Used by the browser runtime's two suppliers of that root — the dev/static
 * route for repo modules and the Service Worker for delivered ones — so they
 * cannot answer the same file differently.
 */
export const MODULE_ASSET_CONTENT_TYPE: Readonly<Record<string, string>> = {
  '.css': 'text/css; charset=utf-8',
  '.html': 'text/html; charset=utf-8',
  '.js': 'text/javascript; charset=utf-8',
  '.json': 'application/json; charset=utf-8',
  '.mjs': 'text/javascript; charset=utf-8',
  '.svg': 'image/svg+xml',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.webm': 'video/webm',
  '.ico': 'image/x-icon',
  '.woff': 'font/woff',
  '.woff2': 'font/woff2',
  '.map': 'application/json; charset=utf-8',
  '.wasm': 'application/wasm',
};

/**
 * Resolve a module code-root file's `Content-Type` from its extension, falling
 * back to `application/octet-stream`. No sniffing — see
 * {@link MODULE_ASSET_CONTENT_TYPE} for why the extension is trusted here.
 */
export function resolveModuleAssetContentType(name: string): string {
  const dot = name.lastIndexOf('.');
  const ext = dot >= 0 ? name.slice(dot).toLowerCase() : '';
  return MODULE_ASSET_CONTENT_TYPE[ext] ?? 'application/octet-stream';
}

/**
 * Resolve a stored file's `Content-Type`: extension first, then the sniffed
 * bytes, then `application/octet-stream`.
 *
 * `header` is optional so a caller that has not read the file yet (or could not)
 * still gets the extension-based answer.
 */
export function resolveMediaContentType(
  name: string,
  header?: Uint8Array,
): string {
  const dot = name.lastIndexOf('.');
  const ext = dot >= 0 ? name.slice(dot).toLowerCase() : '';
  return (
    STORAGE_CONTENT_TYPE[ext] ??
    (header ? sniffMediaMimeFromHeader(header) : undefined) ??
    'application/octet-stream'
  );
}

/** Whether `contentType` is one this app renders as a picture or a movie. */
function isPictureOrMovie(contentType: string | undefined): boolean {
  return (
    contentType !== undefined &&
    (contentType.startsWith('image/') || contentType.startsWith('video/'))
  );
}

/**
 * Whether a file addressed by `@local` (见 {@link MODULE_LOCAL_URL_PREFIX}) may
 * be served — **images and videos only**, by extension.
 *
 * The unguessable token is what stops a renderer from naming an arbitrary file.
 * This predicate is the second door: even a minted token is refused when the
 * path's extension is not an image/video in {@link STORAGE_CONTENT_TYPE}. `.svg`
 * and every markup type are absent from that table by design (same-origin
 * custom scheme + SVG can run script). Audio is out because this route exists
 * for picture and movie bubbles — voice is synthesized in-process and lives
 * under `@storage`.
 */
export function isServableLocalMedia(name: string): boolean {
  const dot = name.lastIndexOf('.');
  const ext = dot >= 0 ? name.slice(dot).toLowerCase() : '';
  return isPictureOrMovie(STORAGE_CONTENT_TYPE[ext]);
}
